import PropTypes from 'prop-types'
import { Button, Switch } from 'antd'
import { FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { formatDOB } from '../../../lib/datetime'
import { toPath } from '../../../routes'
import filenameFromBlobPath from '../../../lib/active-storage'
import DownloadLink from '../../Common/DownloadLink'
import PatientEntry from '../../Common/PatientEntry'

// Width of antd's expand-icon column. The expanded details are indented by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // Billed switch + details link share the last line. Left-aligned rather than
  // space-between: the switch label swaps between "Billed" and "Not Billed", so
  // pinning the link right made the edge jitter from row to row.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Always-visible summary: what a biller scans a cycle for. Line 2 repeats the
// fields the search box matches on (DOB and MRN), line 3 the codes that go on
// the claim.
export function BillingOrderMobileCard({ billingOrder, onBilledChange }) {
  const classes = useStyles()
  const { billingDate, cptCode, id, patient } = billingOrder
  // formatDOB(undefined) would render today's date, so only format real values.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const patientLine = [
    dob,
    patient?.practiceMrn && `MRN ${patient.practiceMrn}`,
  ]
    .filter(Boolean)
    .join(' · ')
  const billingLine = [
    cptCode && `CPT ${cptCode}`,
    billingDate && `Tech DOS ${formatDOB(billingDate)}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {patientLine && <div className={classes.line}>{patientLine}</div>}
      {billingLine && <div className={classes.line}>{billingLine}</div>}
      <div className={classes.actionsLine}>
        <Switch
          checked={billingOrder.billed}
          checkedChildren="Billed"
          unCheckedChildren="Not&nbsp;Billed"
          onChange={isBilled => onBilledChange(id, isBilled)}
        />
        <Link
          to={toPath.billing.remoteBillingOrders.remoteBillingOrder({ id })}
        >
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            Comments / More
          </Button>
        </Link>
      </div>
    </div>
  )
}

BillingOrderMobileCard.propTypes = {
  billingOrder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billed: PropTypes.bool,
    billingDate: PropTypes.string,
    cptCode: PropTypes.string,
    patient: PropTypes.shape({
      birthDate: PropTypes.string,
      practiceMrn: PropTypes.string,
    }),
  }).isRequired,
  onBilledChange: PropTypes.func.isRequired,
}

// Fields hidden behind the per-row expand toggle.
export function BillingOrderMobileDetails({ billingOrder }) {
  const classes = useStyles()
  const { device, icd10Code, preAuthorization, reportSignatureDate } =
    billingOrder

  // Mirrors the desktop "Auth #" column: the number links to the authorization
  // file when there is one.
  let authNumber = '—'
  if (preAuthorization) {
    const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
      preAuthorization
    if (insuranceAuthorizationFilePath) {
      authNumber = (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>
            {insuranceAuthorizationNumber ||
              filenameFromBlobPath(insuranceAuthorizationFilePath)}
          </span>
        </DownloadLink>
      )
    } else {
      authNumber = insuranceAuthorizationNumber || '—'
    }
  }

  return (
    <div className={classes.detailsGrid}>
      <Field label="Device type">
        {device?.deviceModel?.deviceType?.name || '—'}
      </Field>
      <Field label="ICD-10 code">{icd10Code || '—'}</Field>
      <Field label="Pro DOS">
        {reportSignatureDate ? formatDOB(reportSignatureDate) : '—'}
      </Field>
      <Field label="Auth #" span>
        {authNumber}
      </Field>
    </div>
  )
}

BillingOrderMobileDetails.propTypes = {
  billingOrder: PropTypes.shape({
    icd10Code: PropTypes.string,
    reportSignatureDate: PropTypes.string,
    device: PropTypes.shape({
      deviceModel: PropTypes.shape({
        deviceType: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
    preAuthorization: PropTypes.shape({
      insuranceAuthorizationNumber: PropTypes.string,
      insuranceAuthorizationFilePath: PropTypes.string,
    }),
  }).isRequired,
}
